"use client";

import { Link } from "@/components/LocaleLink";
import { useSearchParams } from "next/navigation"
import { useLocaleRouter } from "@/lib/hooks/useLocaleRouter";
import { useState, useEffect, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { auth, detectBrowserLanguage } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import { useLocaleStore, useTranslation } from "@/lib/hooks/useTranslation";
import { Logo } from "@/components/Logo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { loadRecaptchaScript, executeRecaptcha } from "@/lib/recaptcha";
import { isPasswordStrong, PASSWORD_MIN_LENGTH } from "@/lib/passwordPolicy";
import { Gift, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { trackSignUp, trackFunnelStep, trackConversion } from "@/lib/analytics";
import { RecaptchaLegalNotice } from "@/components/RecaptchaLegalNotice";

function RegisterForm() {
  const { tObj, locale } = useTranslation();
  const t = tObj.auth;
  const router = useLocaleRouter();
  const { setAuth, _hasHydrated } = useAuthStore();
  const { setLocale } = useLocaleStore();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [referralInvalid, setReferralInvalid] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const searchParams = useSearchParams();

  const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

  // Load reCAPTCHA script and check referral code on mount
  useEffect(() => {
    loadRecaptchaScript();
    
    // Check for referral code in URL
    const refCode = searchParams.get("ref");
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      setShowReferral(true);
      // Validate the code
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code: string) => {
    try {
      const res = await fetch(`${API_URL}/api/v1/referrals/validate/${code}`);
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          setReferrerName(data.referrer_name);
          setReferralInvalid(false);
          return;
        }
      }
      // Code was typed in full but didn't validate — tell the user instead
      // of letting them register thinking they'd get the bonus.
      setReferrerName(null);
      setReferralInvalid(true);
    } catch (error) {
      console.error("Failed to validate referral code:", error);
      setReferralInvalid(false);
    }
  };

  // Password strength — rough but useful signal. We count character classes
  // and length, cap at 4. We block submit at <2 (i.e. "weak" only).
  const passwordStrength = (() => {
    if (!password) return { score: 0, label: "", tone: "" };
    let score = 0;
    if (password.length >= PASSWORD_MIN_LENGTH) score += 1;
    if (password.length >= 14) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    score = Math.min(score, 4);
    const map = {
      0: { label: locale === "fr" ? "Très faible" : "Very weak", tone: "bg-red-500" },
      1: { label: locale === "fr" ? "Faible" : "Weak", tone: "bg-red-500" },
      2: { label: locale === "fr" ? "Moyen" : "Fair", tone: "bg-yellow-500" },
      3: { label: locale === "fr" ? "Bon" : "Good", tone: "bg-lime-500" },
      4: { label: locale === "fr" ? "Fort" : "Strong", tone: "bg-green-500" },
    } as const;
    return { score, ...map[score as 0 | 1 | 2 | 3 | 4] };
  })();

  // Show loader while hydrating
  if (!_hasHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Logo size="lg" className="animate-pulse" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Detect browser language for new user
    const browserLang = detectBrowserLanguage();

    try {
      // Get reCAPTCHA token
      const recaptcha_token = await executeRecaptcha("register");
      
      // Register API call with detected language
      await auth.register({ email, password, first_name: firstName, last_name: lastName, ui_language: browserLang, recaptcha_token: recaptcha_token || undefined });
      
      // Auto-login after registration
      const tokens = await auth.login({ email, password });
      
      // Get user info — guard against a broken /users/me response.
      const userRes = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/api/v1/users/me`,
        {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        }
      );
      if (!userRes.ok) {
        throw new Error(`users/me failed: ${userRes.status}`);
      }
      const user = await userRes.json();

      // Store auth state
      setAuth(user, tokens.access_token, tokens.refresh_token);
      
      // Set UI locale to match user preference
      setLocale(user.ui_language || browserLang);

      // Apply referral code if present
      if (referralCode) {
        try {
          const refRes = await fetch(`${API_URL}/api/v1/referrals/apply`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokens.access_token}`,
            },
            body: JSON.stringify({ code: referralCode }),
          });
          if (refRes.ok) {
            const refData = await refRes.json();
            if (refData.success) {
              toast.success(refData.message);
            }
          }
        } catch (error) {
          console.error("Failed to apply referral code:", error);
        }
      }
      
      // Track registration success
      trackSignUp('email');
      trackFunnelStep('register_complete');
      trackConversion('registration');

      toast.success(t.registerSuccess);

      // Honor post-register destination. Priority:
      //  1. `?from=ats` → /resumes (pendingATSAnalysis gets picked up there)
      //  2. `?redirect_to=/safe-path` (same-origin relative only)
      //  3. /dashboard (default)
      const rawRedirect = searchParams.get("redirect_to") ?? "";
      const safeRedirect =
        rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
          ? rawRedirect
          : null;
      const fromParam = searchParams.get("from");
      if (fromParam === "ats") {
        router.push("/resumes");
      } else if (safeRedirect) {
        router.push(safeRedirect);
      } else {
        router.push("/dashboard");
      }
    } catch (error: unknown) {
      console.error("Register error:", error);
      const errorMessage = error instanceof Error ? error.message : tObj.common.error;
      if (errorMessage.includes("already")) {
        toast.error(t.emailAlreadyExists);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4 py-8">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <Link href="/" className="mx-auto mb-4">
            <Logo />
          </Link>
          <CardTitle className="text-2xl">{t.register}</CardTitle>
          <CardDescription>
            {locale === "fr" ? "Commence à préparer tes entretiens gratuitement" : "Start preparing for interviews for free"}
          </CardDescription>
          {referrerName && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm">
              <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Gift className="h-3 w-3" />
                {locale === "fr" ? `Invité par ${referrerName}` : `Invited by ${referrerName}`}
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{locale === "fr" ? "Prénom" : "First name"}</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={locale === "fr" ? "Jean" : "John"}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{locale === "fr" ? "Nom" : "Last name"}</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder={locale === "fr" ? "Dupont" : "Doe"}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
              />
              {password.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t.passwordTooShort}
                </p>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < passwordStrength.score
                            ? passwordStrength.tone
                            : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {passwordStrength.label}
                    {!isPasswordStrong(password) && (
                      <span className="ml-1">· {t.passwordTooShort}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
            {showReferral ? (
              <div className="space-y-2">
                <Label htmlFor="referral" className="flex items-center gap-2">
                  <Gift className="h-4 w-4" />
                  {locale === "fr" ? "Code parrain (optionnel)" : "Referral code (optional)"}
                </Label>
                <Input
                  id="referral"
                  type="text"
                  placeholder="VELYQ-XXXXXX"
                  value={referralCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    setReferralCode(code);
                    setReferralInvalid(false);
                    if (code.length >= 10) {
                      validateReferralCode(code);
                    } else {
                      setReferrerName(null);
                    }
                  }}
                  className="font-mono"
                  autoFocus
                />
                {referrerName && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {locale === "fr" ? `Invité par ${referrerName}` : `Invited by ${referrerName}`}
                  </p>
                )}
                {referralInvalid && !referrerName && (
                  <p className="text-xs text-yellow-500">
                    {locale === "fr"
                      ? "Ce code n'est pas reconnu. Vérifie qu'il est correct."
                      : "This code isn't recognized. Double-check it."}
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowReferral(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
              >
                <Gift className="h-3.5 w-3.5" />
                {locale === "fr" ? "J'ai un code parrain" : "I have a referral code"}
              </button>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !isPasswordStrong(password)}
            >
              {isLoading ? tObj.common.loading : t.register}
            </Button>
            <div className="mt-2 text-center">
              <RecaptchaLegalNotice />
            </div>
          </form>

          <div className="my-6 flex items-center gap-4">
            <Separator className="flex-1" />
            <span className="text-sm text-muted-foreground">{tObj.common.or}</span>
            <Separator className="flex-1" />
          </div>

          <GoogleSignInButton />

          <p className="mt-4 text-center text-xs text-muted-foreground">
            {locale === "fr" ? (
              <>
                En créant un compte, tu acceptes nos{" "}
                <Link href="/terms" className="underline hover:text-primary">
                  Conditions d&apos;utilisation
                </Link>{" "}
                et notre{" "}
                <Link href="/privacy" className="underline hover:text-primary">
                  Politique de confidentialité
                </Link>
                .
              </>
            ) : (
              <>
                By creating an account, you agree to our{" "}
                <Link href="/terms" className="underline hover:text-primary">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="/privacy" className="underline hover:text-primary">
                  Privacy Policy
                </Link>
                .
              </>
            )}
          </p>
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            {t.hasAccount}{" "}
            <Link href="/login" className="font-medium text-primary hover:underline">
              {t.login}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    }>
      <RegisterForm />
    </Suspense>
  );
}
